import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import { AuthProvider } from './context/AuthContext';
import Header from './components/Header';
import StepNavigation from './components/StepNavigation';
import UploadStep from './components/UploadStep';
import DesignStep from './components/DesignStep';
import BackgroundStep from './components/BackgroundStep';
import PersonalizeStep from './components/PersonalizeStep';
import GenerateStep from './components/GenerateStep';
import ShirtUpload from './components/ShirtUpload';
import MyVideos from './components/MyVideos';
import EmailVerification from './components/EmailVerification';
import ResetPassword from './components/ResetPassword';
import GoogleCallback from './components/GoogleCallback';

function MainApp() {
  const [currentStep, setCurrentStep] = useState(1);
  const [photos, setPhotos] = useState([]);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [personalization, setPersonalization] = useState({
    names: '',
    customText: '',
    color: '#667eea',
    logo: null,
    logoUUID: ''
  });

  const handleNext = () => {
    setCurrentStep(prev => Math.min(prev + 1, 5));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleStepClick = (step) => {
    if (step <= currentStep) {
      setCurrentStep(step);
    }
  };

  const handleReset = () => {
    setCurrentStep(1);
    setPhotos([]);
    setSelectedDesign(null);
    setSelectedBackground(null);
    setPersonalization({
      names: '',
      customText: '',
      color: '#667eea',
      logo: null,
      logoUUID: ''
    });
  };

  return (
    <AuthProvider>
      <div className="App">
          <Header onLogoClick={handleReset} />
      
      <div className="container">

        <div className="title-section">
          <h2>Create Your AI Fashion Video</h2>
          <p>
            Upload photos of yourself, friends, or family (up to 5 people). Choose themed designs,
            <br />
            personalize with names or logos, and generate viral AI videos.
          </p>
        </div>

        <StepNavigation 
          currentStep={currentStep} 
          onStepClick={handleStepClick}
        />

        {currentStep === 1 && (
          <DesignStep
            selectedDesign={selectedDesign}
            setSelectedDesign={setSelectedDesign}
            onNext={handleNext}
          />
        )}

        {currentStep === 2 && (
          <BackgroundStep
            selectedBackground={selectedBackground}
            setSelectedBackground={setSelectedBackground}
            onNext={handleNext}
            onBack={handleBack}
          />
        )}

        {currentStep === 3 && (
          <UploadStep
            photos={photos}
            setPhotos={setPhotos}
            onNext={handleNext}
            onBack={handleBack}
            uploading={uploading}
            setUploading={setUploading}
          />
        )}

        {currentStep === 4 && (
          <PersonalizeStep
            personalization={personalization}
            setPersonalization={setPersonalization}
            onNext={handleNext}
            onBack={handleBack}
          />
        )}

        {currentStep === 5 && (
          <GenerateStep
            photos={photos}
            selectedDesign={selectedDesign}
            selectedBackground={selectedBackground}
            personalization={personalization}
            onBack={handleBack}
            onReset={handleReset}
          />
        )}

        <div className="features">
          <div className="feature-card">
            <div className="feature-icon">👥</div>
            <div className="feature-content">
              <h3>1. Multi-Person Support</h3>
              <p>Upload photos with up to 5 people. AI detects each person individually for perfect fitting.</p>
            </div>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🎨</div>
            <div className="feature-content">
              <h3>2. Full Personalization</h3>
              <p>Add names, logos, or custom text with your choice of fonts, colors, and placement.</p>
            </div>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📱</div>
            <div className="feature-content">
              <h3>3. Viral Social Sharing</h3>
              <p>One-tap sharing to TikTok, Instagram, and more with auto-generated hashtags.</p>
            </div>
          </div>
        </div>

        <div className="footer">
          <p>PakaiNow AI - #MyAITee #WearTheFuture.</p>
          
        </div>
      </div>
      </div>
    </AuthProvider>
  );
}

function App() {
  return (
    <Router>
        <Routes>
        <Route path="/" element={<MainApp />} />
        <Route path="/auth/callback" element={
          <AuthProvider>
            <GoogleCallback />
          </AuthProvider>
        } />
        <Route path="/verify-email" element={
          <AuthProvider>
            <EmailVerification />
          </AuthProvider>
        } />
        <Route path="/reset-password" element={
          <AuthProvider>
            <ResetPassword />
          </AuthProvider>
        } />
        <Route path="/admin" element={
          <AuthProvider>
            <div className="App">
              <Header />
              <ShirtUpload />
            </div>
          </AuthProvider>
        } />
        <Route path="/my-videos" element={
          <AuthProvider>
            <div className="App">
              <Header />
              <MyVideos />
            </div>
          </AuthProvider>
        } />
        <Route path="/my-videos/:videoId" element={
          <AuthProvider>
            <div className="App">
              <Header />
              <MyVideos />
            </div>
          </AuthProvider>
        } />
      </Routes>
    </Router>
  );
}

export default App;
